// credentialsService.js - Supporting dev, uat, and prod environments
// Create credential value containers
let bucketNameValue = '';
let regionValue = '';
let accessKeyIdValue = '';
let secretAccessKeyValue = '';
let cognitoUserPoolIdValue = '';
let cognitoAppClientIdValue = '';

let credentialStatus = 'idle'; // idle, loading, loaded, error
let subscribers = [];
const REFRESH_INTERVAL = 60 * 60 * 1000; // 1 hour in milliseconds

// Detect the current environment from build-time variable
// This variable should be set in your .env.* files
const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT;
console.log("env", ENVIRONMENT);

// API endpoint configuration for all environments
const API_ENDPOINTS = {
  dev: 'https://hzllemfku7.execute-api.us-east-1.amazonaws.com/dev/fetchCredweb',
  uat: 'https://bahu18fk9a.execute-api.us-east-1.amazonaws.com/uat/fetchCredweb', // Update with your UAT endpoint
  prod: 'https://7549jb05eg.execute-api.us-east-1.amazonaws.com/prod/fetchCredweb'
};

/**
 * Get the current environment 
 * @returns {string} Current environment ('dev', 'uat', or 'prod')
 */
export function getEnvironment() {
  return ENVIRONMENT;
}

/**
 * Get the API endpoint for the current environment
 * @returns {string} API endpoint URL
 */
function getApiEndpoint() {
  if (!API_ENDPOINTS[ENVIRONMENT]) {
    console.warn(`No API endpoint defined for environment: ${ENVIRONMENT}, falling back to prod`);
    return API_ENDPOINTS.prod;
  }
  return API_ENDPOINTS[ENVIRONMENT];
}

/**
 * Notify all subscribers that credentials have changed
 */
function notifySubscribers() {
  const credentials = {
    bucketName: bucketNameValue,
    region: regionValue,
    accessKeyId: accessKeyIdValue,
    secretAccessKey: secretAccessKeyValue,
    cognitoUserPoolId: cognitoUserPoolIdValue,
    cognitoAppClientId: cognitoAppClientIdValue,
    environment: ENVIRONMENT
  };
  subscribers.forEach(callback => callback(credentials));
}

/**
 * Subscribe to credential changes
 * @param {Function} callback Function to call when credentials change
 * @returns {Function} Unsubscribe function
 */
export function subscribeToCredentials(callback) {
  subscribers.push(callback);
  return () => {
    subscribers = subscribers.filter(cb => cb !== callback);
  };
}

/**
 * Fetch API credentials from the server
 * @param {string} token Authentication token
 * @param {boolean} force Force refresh even if credentials exist
 * @returns {Promise<object|null>} Credentials object or null if failed
 */
async function fetchAPICredentials(token, force = false) {
  try {
    credentialStatus = 'loading';
    notifySubscribers();
    
    const apiEndpoint = getApiEndpoint();
    console.log(`Fetching credentials from ${apiEndpoint} (${ENVIRONMENT} environment)`);
    
    const response = await fetch(apiEndpoint, {
      method: 'GET',
      headers: {
        'Authorization': token,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log("pooll", data);
    if (data.status === 200 && data.data && data.data.length > 0) {
      // Update the credential values
      bucketNameValue = data.data[0].bucket || bucketNameValue;
      regionValue = data.data[0].region || regionValue;
      accessKeyIdValue = data.data[0].accessKeyId || accessKeyIdValue;
      secretAccessKeyValue = data.data[0].secretAccessKey || secretAccessKeyValue;
      cognitoUserPoolIdValue = data.data[0].cognitoUserPoolId || cognitoUserPoolIdValue;
      cognitoAppClientIdValue = data.data[0].cognitoAppClientId || cognitoAppClientIdValue;
      
      credentialStatus = 'loaded';
      notifySubscribers();
      console.log(`Credentials loaded from API (${ENVIRONMENT} environment)`);
      
      return {
        bucketName: bucketNameValue,
        region: regionValue,
        accessKeyId: accessKeyIdValue,
        secretAccessKey: secretAccessKeyValue,
        cognitoUserPoolId: cognitoUserPoolIdValue,
        cognitoAppClientId: cognitoAppClientIdValue,
        environment: ENVIRONMENT
      };
    } else {
      throw new Error('Invalid credential format in API response');
    }
  } catch (error) {
    credentialStatus = 'error';
    notifySubscribers();
    console.error(`Error loading credentials from ${ENVIRONMENT} environment:`, error);
  }
  return null;
}

/**
 * Initialize credentials with retry logic
 * @param {number} maxRetries Maximum number of retries
 * @param {number} retryDelay Delay between retries in milliseconds
 * @returns {Promise<boolean>} Success status
 */
export async function initializeCredentials(token, maxRetries = 3, retryDelay = 2000) {
  // Use the provided token parameter instead of localStorage
  if (!token) {
    console.warn("No auth token provided for credential initialization");
    return false;
  }
  
  let retries = 0;
  let success = false;
  
  while (retries < maxRetries && !success) {
    try {
      const result = await fetchAPICredentials(token, retries > 0);
      if (result) {
        success = true;
        
        // Set up periodic refresh
        scheduleCredentialRefresh(token);
      } else {
        throw new Error(`Failed to fetch credentials from ${ENVIRONMENT} environment`);
      }
    } catch (error) {
      console.error(`Credential initialization attempt ${retries + 1} failed:`, error);
      retries++;
      
      if (retries < maxRetries) {
        console.log(`Retrying in ${retryDelay}ms...`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
  }
  
  return success;
}

/**
 * Set up periodic credential refresh
 * @param {string} token Authentication token
 */
function scheduleCredentialRefresh(token) {
  // Refresh credentials periodically
  setInterval(async () => {
    console.log(`Refreshing API credentials for ${ENVIRONMENT} environment`);
    try {
      await fetchAPICredentials(token, true);
    } catch (error) {
      console.error(`Failed to refresh credentials for ${ENVIRONMENT} environment:`, error);
    }
  }, REFRESH_INTERVAL);
}
/**
 * Get current credential status
 * @returns {string} Current status (idle, loading, loaded, error)
 */
export function getCredentialStatus() {
  return credentialStatus;
}

/**
 * Get the current credentials
 * @returns {object} Current credentials
 */
export function getCredentials() {
  return {
    bucketName: bucketNameValue,
    region: regionValue,
    accessKeyId: accessKeyIdValue,
    secretAccessKey: secretAccessKeyValue,
    cognitoUserPoolId: cognitoUserPoolIdValue,
    cognitoAppClientId: cognitoAppClientIdValue,
    environment: ENVIRONMENT
  };
}

/**
 * Get Cognito configuration object for AWS Amplify
 * @returns {object} Cognito configuration
 */
export function getCognitoConfig() {
  return {
    cognito: {
      REGION: regionValue || "us-east-1",
      USER_POOL_ID: cognitoUserPoolIdValue,
      APP_CLIENT_ID: cognitoAppClientIdValue
    }
  };
}

/**
 * Force refresh of credentials
 * @returns {Promise<boolean>} Success status
 */
export async function refreshCredentials(token) {
  if (!token) return false;
  
  try {
    const result = await fetchAPICredentials(token, true);
    return !!result;
  } catch (error) {
    return false;
  }
}

// Export functions that return the values when called,
// but also work as properties for direct assignment
// This is a trick to make the functions both callable and usable as values

function createValueFunction(getter) {
  const fn = () => getter();
  fn.toString = () => getter();
  fn.valueOf = () => getter();
  return fn;
}

// Export getter functions with special behavior
export const getBucketName = createValueFunction(() => bucketNameValue);
export const getRegion = createValueFunction(() => regionValue);
export const getAccessKeyId = createValueFunction(() => accessKeyIdValue);
export const getSecretAccessKey = createValueFunction(() => secretAccessKeyValue);
export const getCognitoUserPoolId = createValueFunction(() => cognitoUserPoolIdValue);
export const getCognitoAppClientId = createValueFunction(() => cognitoAppClientIdValue);